import React from 'react';
import SettingsCard from '../../organisms/SettingsCard';
import Input from '../../atoms/Input';
import Select from '../../atoms/Select';
import Button from '../../atoms/Button';
import Toggle from '../../atoms/Toggle';
import DragHandleIcon from '../../atoms/DragHandleIcon';
import TrashIcon from '../../atoms/TrashIcon';
import FieldLabel from '../../atoms/FieldLabel';
import { useSettingsWizard } from '../../../context/SettingsWizardContext';
import { FIELD_LIBRARY } from '../../../data/settingsSteps';
import Stepper from '../../organisms/Stepper';
import { SETTINGS_STEPS } from '../../../data/settingsSteps';

const FIELD_TYPE_OPTIONS = FIELD_LIBRARY.map((f) => ({
  value: f.id,
  label: f.label,
}));

function FieldLibraryIcon({ type }) {
  const map = {
    text: 'A',
    doc: '¶',
    calendar: '口',
    clock: '◷',
    chevron: '▾',
  };
  return <span className="ca-field-lib__glyph">{map[type] || '▾'}</span>;
}

export default function FormFieldsStep({ stepMeta, stepperProps }) {
  const { state, update } = useSettingsWizard();
  const { fieldDraft, formFields } = state;

  const setDraft = (patch) => update({ fieldDraft: { ...fieldDraft, ...patch } });

  const addField = () => {
    if (!fieldDraft.name.trim() || !fieldDraft.type) return;
    const typeLabel =
      FIELD_TYPE_OPTIONS.find((o) => o.value === fieldDraft.type)?.label || fieldDraft.type;
    update({
      formFields: [
        ...formFields,
        {
          id: `${Date.now()}`,
          name: fieldDraft.name.trim(),
          type: fieldDraft.type,
          typeLabel,
          required: false,
        },
      ],
      fieldDraft: { name: '', type: '' },
    });
  };

  const patchField = (id, patch) => {
    update({
      formFields: formFields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    });
  };

  const removeField = (id) => {
    update({ formFields: formFields.filter((f) => f.id !== id) });
  };

  const applyLibraryType = (item) => {
    setDraft({ type: item.id });
  };

  return (
    <>
      <Stepper {...stepperProps} steps={SETTINGS_STEPS} />
      <SettingsCard
      title={stepMeta.title}
      description={stepMeta.description}
      showNext={false}
    >
      <div className="ca-form-fields">
        <aside className="ca-field-lib">
          <h3 className="ca-field-lib__title">Field Library</h3>
          <p className="ca-small">Drag and drop the fields to add.</p>
          <ul className="ca-field-lib__list">
            {FIELD_LIBRARY.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="ca-field-lib__item"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/plain', item.id);
                  }}
                  onClick={() => applyLibraryType(item)}
                >
                  <FieldLibraryIcon type={item.icon} />
                  <span>{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <div className="ca-form-fields__main">
          <div
            className="ca-add-fields"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const type = e.dataTransfer.getData('text/plain');
              if (type) setDraft({ type });
            }}
          >
            <div className="ca-add-fields__top">
              <h3 className="ca-add-fields__title">Add Fields</h3>
              <Button onClick={addField}>Add Field</Button>
            </div>
            <div className="ca-step-row">
              <div className="ca-step-row__grow">
                <FieldLabel>Name of Field</FieldLabel>
                <Input
                  value={fieldDraft.name}
                  onChange={(e) => setDraft({ name: e.target.value })}
                  placeholder="Enter name of the field."
                />
              </div>
              <div className="ca-step-row__grow">
                <FieldLabel>Type of Field</FieldLabel>
                <Select
                  value={fieldDraft.type}
                  onChange={(e) => setDraft({ type: e.target.value })}
                  options={FIELD_TYPE_OPTIONS}
                  placeholder="Select type of the field."
                />
              </div>
            </div>
          </div>

          <div className="ca-fields-table">
            <div className="ca-fields-table__head">
              <div>Field Name</div>
              <div>Field Type</div>
              <div>Required</div>
              <div />
            </div>
            {formFields.length === 0 ? (
              <div className="ca-fields-table__empty">
                <p className="ca-small">No fields added yet.</p>
              </div>
            ) : (
              formFields.map((field) => (
                <div className="ca-fields-table__row" key={field.id}>
                  <div className="ca-fields-table__name">
                    <DragHandleIcon />
                    <Input
                      value={field.name}
                      onChange={(e) => patchField(field.id, { name: e.target.value })}
                    />
                  </div>
                  <Select
                    value={field.type}
                    onChange={(e) => {
                      const type = e.target.value;
                      const typeLabel =
                        FIELD_TYPE_OPTIONS.find((o) => o.value === type)?.label || type;
                      patchField(field.id, { type, typeLabel });
                    }}
                    options={FIELD_TYPE_OPTIONS}
                    placeholder="Select type"
                  />
                  <Toggle
                    checked={field.required}
                    onChange={(required) => patchField(field.id, { required })}
                    aria-label="Required"
                  />
                  <button
                    type="button"
                    className="ca-icon-btn"
                    aria-label="Delete field"
                    onClick={() => removeField(field.id)}
                  >
                    <TrashIcon />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      </SettingsCard>
    </>
  );
}
